import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import { message } from "antd";
import { RcFile } from "antd/lib/upload";
import { Dayjs } from "dayjs";
import escape from "lodash/escape";
import { v4 as uuid } from "uuid";
import { ResponseErr } from "../../common/services/AppClient";
import { Utils } from "../../common/misc/Utils";
import FollowUpTaskService, {
    FollowUpTaskOptions,
} from "../services/FollowUpTaskService";

// Project and Title are required form rules, so validated values always carry them.
export type FollowUpTaskValues = {
    projectId: string;
    title: string;
    description?: string;
    assignedTo?: string;
    dueDate?: Dayjs;
    taskType?: string;
};

export const followUpDescription = (options: FollowUpTaskOptions) => {
    const lines = [`Case: ${options.caseLabel} · ${options.subjectKind}`];
    const submission = [
        options.submissionId ? `Submission: ${options.submissionId}` : null,
        options.uwYear ? `UW year: ${options.uwYear}` : null,
    ].filter(Boolean);
    if (submission.length) lines.push(submission.join(" · "));
    return `${lines.join("\n")}\n\n`;
};

// A rejected retry says nothing about an earlier unknown outcome. Keep its identity
// unless the server explicitly confirms a terminal outcome with DefiniteFailureCode.
// Without an earlier unknown outcome, 400/403/413 allow correcting a new request.
const definiteFailureCode = "follow-up-definite-failure";
const isDefinite = (failure: ResponseErr, recovering: boolean) =>
    (!recovering && [400, 403, 413].includes(failure.status)) ||
    (failure.status === 404 && failure.data?.errorCode === definiteFailureCode);

// Backend exceptions (400/403/404/409) all arrive as the shared ErrorMessage shape `{ title }`.
const errorMessage = (failure: ResponseErr): string =>
    typeof failure.data?.title === "string"
        ? failure.data.title
        : "Could not create the task. Please retry.";

type Access =
    | { status: "loading" }
    | { status: "failed" }
    | { status: "ready"; options: FollowUpTaskOptions };
type Editing = { status: "editing"; requestId: string; error?: string };
type Recovery = {
    status: "recovery";
    requestId: string;
    payload: FormData;
    error: string;
};
type Dialog =
    // A closed recovery keeps its request ID and payload so reopening cannot mint a duplicate.
    | { status: "closed"; recovery?: Recovery }
    | Editing
    | Recovery
    | { status: "saving"; previous: Editing | Recovery; payload: FormData };

export default class FollowUpTaskCreationStore {
    access: Access = { status: "loading" };
    dialog: Dialog = { status: "closed" };
    files: RcFile[] = [];
    private subjectId = "";
    private session = 0;
    private loadSequence = 0;
    private submitting = false;

    constructor(private service: FollowUpTaskService) {
        makeObservable(this, {
            access: observable.ref,
            dialog: observable.ref,
            files: observable.ref,
            options: computed,
            open: computed,
            saving: computed,
            recovering: computed,
            error: computed,
            unavailable: computed,
            actionDisabled: computed,
            initialize: action.bound,
            dispose: action.bound,
            start: action.bound,
            save: action.bound,
            cancel: action.bound,
            discard: action.bound,
            rejectFile: action.bound,
            addFile: action.bound,
            removeFile: action.bound,
        });
    }

    initialize(subjectId: string) {
        this.subjectId = subjectId;
        this.session++;
        this.submitting = false;
        this.dialog = { status: "closed" };
        return this.load();
    }

    dispose() {
        this.session++;
        this.loadSequence++;
    }

    private async load() {
        const session = this.session;
        const sequence = ++this.loadSequence;
        const isCurrent = () =>
            this.session === session && this.loadSequence === sequence;
        runInAction(() => {
            this.access = { status: "loading" };
        });
        try {
            const result = await this.service.options(this.subjectId);
            if (!isCurrent()) return;
            if (result.isErr()) {
                runInAction(() => {
                    this.access = { status: "failed" };
                });
                return;
            }
            runInAction(() => {
                this.access = { status: "ready", options: result.value };
            });
            return result.value;
        } catch {
            if (isCurrent())
                runInAction(() => {
                    this.access = { status: "failed" };
                });
        }
        return undefined;
    }

    async start(initializeForm: (options: FollowUpTaskOptions) => void) {
        if (this.dialog.status === "closed" && this.dialog.recovery) {
            this.dialog = this.dialog.recovery;
            return;
        }
        const session = this.session;
        const openDialog = this.access.status !== "failed";
        const options = await this.load();
        if (
            this.session !== session ||
            !openDialog ||
            !options?.projects.length
        )
            return;
        initializeForm(options);
        runInAction(() => {
            this.files = [];
            this.dialog = { status: "editing", requestId: uuid() };
        });
    }

    async save(
        validate: () => Promise<FollowUpTaskValues>,
        onCreated: () => Promise<void>,
    ) {
        if (
            this.submitting ||
            (this.dialog.status !== "editing" &&
                this.dialog.status !== "recovery")
        )
            return;
        const previous = this.dialog;
        const session = this.session;
        const subjectId = this.subjectId;
        const isCurrent = () => this.session === session;
        this.submitting = true;
        try {
            let payload: FormData;
            if (previous.status === "recovery") {
                payload = previous.payload;
            } else {
                const values = await validate();
                payload = new FormData();
                payload.append("requestId", previous.requestId);
                payload.append("projectId", values.projectId);
                payload.append("title", values.title.trim());
                payload.append(
                    "description",
                    escape(values.description ?? "").replace(
                        /\r\n|\r|\n/g,
                        "<br>",
                    ),
                );
                if (values.assignedTo)
                    payload.append("assignedTo", values.assignedTo);
                if (values.taskType)
                    payload.append("taskType", values.taskType);
                if (values.dueDate)
                    payload.append(
                        "dueDate",
                        Utils.formatDateAsUtcMidnightIso(values.dueDate)!,
                    );
                this.files.forEach((file) =>
                    payload.append("attachments", file),
                );
            }
            if (!isCurrent()) return;
            runInAction(() => {
                this.dialog = { status: "saving", previous, payload };
            });
            try {
                const result = await this.service.create(subjectId, payload);
                if (!isCurrent()) return;
                if (result.isErr()) {
                    const failure = result.error;
                    runInAction(() => {
                        this.dialog = isDefinite(
                            failure,
                            previous.status === "recovery",
                        )
                            ? {
                                  // Corrected values are a new request.
                                  status: "editing",
                                  requestId: uuid(),
                                  error: errorMessage(failure),
                              }
                            : {
                                  status: "recovery",
                                  requestId: previous.requestId,
                                  payload,
                                  error: errorMessage(failure),
                              };
                    });
                    return;
                }
                runInAction(() => {
                    this.dialog = { status: "closed" };
                });
                message.success("Follow-up task created");
                void Promise.resolve()
                    .then(async () => {
                        if (isCurrent()) await onCreated();
                    })
                    .catch(() => {
                        if (isCurrent())
                            message.warning(
                                "Task created. Refresh the case to see it.",
                            );
                    });
            } catch {
                if (isCurrent())
                    runInAction(() => {
                        this.dialog = {
                            status: "recovery",
                            requestId: previous.requestId,
                            payload,
                            error: "The save result is unknown. Retry to recover the same task.",
                        };
                    });
            }
        } finally {
            if (this.session === session) this.submitting = false;
        }
    }

    cancel() {
        if (this.saving) return;
        if (this.dialog.status === "recovery") {
            this.dialog = { status: "closed", recovery: this.dialog };
            return;
        }
        this.dialog = { status: "closed" };
        this.files = [];
    }

    /** Explicitly abandon a recovery whose outcome stays unknown. */
    discard() {
        if (this.saving) return;
        this.dialog = { status: "closed" };
        this.files = [];
    }

    rejectFile(error: string) {
        if (this.dialog.status === "editing")
            this.dialog = { ...this.dialog, error };
    }

    addFile(file: RcFile) {
        this.files = [...this.files, file];
    }

    removeFile(uid: string) {
        this.files = this.files.filter((file) => file.uid !== uid);
    }

    get options() {
        return this.access.status === "ready" ? this.access.options : undefined;
    }
    get open() {
        return this.dialog.status !== "closed";
    }
    get saving() {
        return this.dialog.status === "saving";
    }
    private get activeDialog() {
        return this.dialog.status === "saving"
            ? this.dialog.previous
            : this.dialog;
    }
    get recovering() {
        return this.activeDialog.status === "recovery";
    }
    get error() {
        const active = this.activeDialog;
        return !this.saving &&
            (active.status === "editing" || active.status === "recovery")
            ? active.error
            : undefined;
    }
    get unavailable() {
        return this.access.status === "loading"
            ? "Checking task creation access\u2026"
            : this.access.status === "failed"
              ? "Could not load task creation options. Click to retry."
              : !this.options?.projects.length
                ? "You do not have a project in which you can create a task."
                : undefined;
    }
    get actionDisabled() {
        return (
            this.access.status === "loading" ||
            (this.access.status === "ready" &&
                !this.access.options.projects.length)
        );
    }
}
